import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ─── Types (real, multi-tenant; mirror the vv-demo admin entities) ───────────

export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed';
export type Channel = 'direct' | 'airbnb' | 'booking' | 'vrbo';
export type DepositStatus = 'scheduled' | 'held' | 'released' | 'claimed' | 'action_required' | 'failed';

export interface Property {
  id: number;
  name: string;
  registration_number: string | null;
  property_type: string | null;
  max_guests: number | null;
  bedrooms: number | null;
  beds: number | null;
  bathrooms: number | null;
  surface_m2: number | null;
  address_street: string | null;
  post_code: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  check_in_time: string | null;
  check_out_time: string | null;
  min_nights: number | null;
  max_nights: number | null;
  wifi_name: string | null;
  wifi_password: string | null;
  base_price: number | null;
  cleaning_fee: number | null;
  security_deposit: number | null;
  // Length-of-stay discounts (migration 039)
  discount_weekly_percent: number | null;  // % off for stays >= 7 nights
  discount_monthly_percent: number | null; // % off for stays >= 28 nights
  // Length-of-stay price tiers (migration 057) — JSON array of
  // { min_nights, price_per_night }; absolute per-night prices by stay length.
  price_tiers_json: string | null;
  description: string | null;
  amenities: string | null;
  amenities_json: string | null;
  house_rules: string | null;
  cancellation_policy: string | null;
  neighborhood_tips: string | null;
  access_instructions: string | null;
  // Guest-app (digital guide) fields
  checkin_instructions: string | null;
  checkout_instructions: string | null;
  door_code: string | null;
  lock_info: string | null;
  emergency_contact: string | null;
  house_manual: string | null;
  local_guide: string | null;
  parking_info: string | null;
  guest_sections_json: string | null;   // admin-managed guide config (migration 056)
  guest_app_enabled: number | null;
  guest_messaging_enabled: number | null;
  ical_import_booking: string | null;
  ical_import_airbnb: string | null;
  ical_import_vrbo: string | null;
  ical_import_other: string | null;
  guest_token: string | null;
  // Owner of the unit (for agencies managing on behalf of end-owners).
  owner_name: string | null;
  owner_email: string | null;
  owner_id: number | null;              // FK to owners (migration 054)
  owner_display_name?: string | null;   // joined owner name (read-only, from GET)
  // SES.Hospedajes mapping (migration 051)
  ses_account_id: number | null;
  ses_codigo_establecimiento: string | null;
  status: string;
}

export interface PropertyMedia {
  id: number;
  kind: 'photo' | 'video' | 'document';
  filename: string | null;
  content_type: string | null;
  size: number | null;
  created_at: string;
}

export interface Booking {
  id: number;
  property_id: number | null;
  property_name?: string | null;
  code: string | null;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  guest_country: string | null;
  check_in: string;
  check_out: string;
  guests: number;
  nights: number;
  price_per_night: number;
  cleaning_fee: number;
  total: number;
  status: BookingStatus;
  channel: Channel;
  notes: string | null;
  // Payment options (migration 038)
  amount_paid: number | null;
  balance_due: number | null;
  tourist_tax: number | null;
  payment_method: string | null;          // 'stripe' | 'transfer'
  // Automatic balance collection for deposit bookings (migration 042)
  balance_charge_status: 'scheduled' | 'paid' | 'failed' | 'action_required' | null;
  balance_charged_at: string | null;
  // Guest registration — parte de viajeros (migration 040)
  registration_completed_at: string | null;
  // SES.Hospedajes (migration 051): latest submission state + whether the
  // property is mapped to a SES account (null = property not configured).
  ses_estado?: 'sent' | 'confirmed' | 'rejected' | 'error' | null;
  ses_account_id?: number | null;
  // Computed by the bookings list endpoint (subqueries).
  agreement_signed_at?: string | null;
  review_submitted?: number;
  // Paid extras + damage deposit
  extras_total: number | null;
  deposit_amount: number | null;
  deposit_status: DepositStatus | null;
  deposit_payment_intent_id: string | null;
  deposit_held_at: string | null;
  deposit_released_at: string | null;
  deposit_last_error: string | null;
  recovery_sent_at: string | null;
  created_at: string;
}

// ─── Paid extras (per-property upsells offered in the booking flow) ──────────
export type ExtraPricingType = 'per_stay' | 'per_night' | 'per_guest';

export interface Extra {
  id: number;
  client_id: number;
  property_id: number;
  name: string;
  name_i18n: string | null;
  description: string | null;
  price: number;
  pricing_type: ExtraPricingType;
  active: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Line items of a specific booking (snapshot of what the guest purchased).
export interface BookingExtraLine {
  id: number;
  name: string;
  pricing_type: ExtraPricingType;
  unit_price: number;
  quantity: number;
  total: number;
}

// House-rules acceptance record captured during checkout.
export interface BookingAgreement {
  accepted_name: string;
  guest_email: string | null;
  doc_version: string | null;
  rules_hash: string | null;
  language: string | null;
  ip_address: string | null;
  user_agent: string | null;
  accepted_at: string;
  rules_snapshot: string | null;
}

export interface ClaimFile {
  id: number;
  filename: string | null;
  content_type: string | null;
  size: number | null;
}

export interface DepositClaim {
  id: number;
  amount: number;
  description: string | null;
  status: 'captured' | 'failed' | 'pending';
  created_at: string;
  files: ClaimFile[];
}

export interface BookingDetail {
  booking: Booking;
  extras: BookingExtraLine[];
  agreement: BookingAgreement | null;
  claims: DepositClaim[];
}

export interface Season {
  id: number;
  property_id: number | null;
  name: string;
  start_month: number;
  end_month: number;
  price_per_night: number;
  min_stay: number;
  sort_order: number;
}

export interface PromoCode {
  id: number;
  property_id: number | null;  // null = applies to all the tenant's properties
  code: string;
  discount_percent: number | null;
  discount_fixed: number | null;
  valid_from: string | null;
  valid_until: string | null;
  max_uses: number | null;
  used_count: number;
  active: number;
  created_at: string;
}

export interface BlockedDate {
  id: number;
  property_id: number | null;
  start_date: string;
  end_date: string;
  reason: string | null;
  source: string;
}

export interface GuestMessage {
  id: number;
  booking_id: number | null;
  guest_name: string;
  guest_email: string | null;
  subject: string | null;
  preview: string | null;
  body: string | null;
  channel: 'direct' | 'airbnb' | 'booking';
  is_read: number;
  created_at: string;
}

export interface MessageReply {
  id: number;
  message_id: number;
  body: string;
  from_admin: number;
  created_at: string;
}

export type ReviewSource = 'direct' | 'airbnb' | 'booking' | 'google' | 'other';
export type ReviewStatus = 'published' | 'pending' | 'hidden';

export interface Review {
  id: number;
  property_id: number | null;   // null = applies to all / the business
  property_name?: string | null;
  author_name: string;
  author_country: string | null;
  rating: number;               // 1..5
  title: string | null;
  body: string | null;
  source: ReviewSource;
  review_date: string | null;
  status: ReviewStatus;
  booking_id: number | null;    // set when a guest submitted it from a stay
  created_at: string;
}

export interface ManageDashboard {
  kpis: { revenue: number; activeBookings: number; pendingBookings: number; totalGuests: number; properties: number };
  revenueByMonth: { month: string; revenue: number }[];
  upcoming: Booking[];
  recent: Booking[];
}

// ─── Analytics (owner portal) ─────────────────────────────────────────────────
export type AnalyticsPeriod = '30d' | '90d' | '12m' | 'all';

export interface Analytics {
  period: AnalyticsPeriod;
  kpis: {
    totalBookings: number;
    confirmed: number;
    pending: number;
    cancelled: number;
    completed: number;
    revenue: number;
    collected: number;
    avgBookingValue: number;
    nights: number;
    avgStayLength: number;
    avgLeadTime: number;
    occupancy: number | null;
    upcoming: number;
  };
  revenueByMonth: { month: string; revenue: number; bookings: number }[];
  byStatus: { confirmed: number; pending: number; cancelled: number; completed: number };
  channelMix: { channel: Channel; count: number; revenue: number }[];
  nightsByProperty: { property_id: number | null; name: string; nights: number }[];
}

// ─── Fetch helper ────────────────────────────────────────────────────────────
const API = '/api/portal/manage';
async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...options.headers, ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
    credentials: 'same-origin',
  });
  const data = await res.json() as T & { error?: string };
  if (!res.ok) throw new Error(data.error || 'error');
  return data;
}

// Scope helper: a property_id query-string fragment + a stable key segment.
const pidQs = (pid?: number | null) => (pid ? `property_id=${pid}` : '');
const pidKey = (pid?: number | null) => (pid ? pid : 'all');

export const mk = {
  properties: ['manage', 'properties'] as const,
  bookings: (s?: string, pid?: number | null) => ['manage', 'bookings', s ?? 'all', pidKey(pid)] as const,
  booking: (id: number) => ['manage', 'booking', id] as const,
  blocked: (pid?: number | null) => ['manage', 'blocked', pidKey(pid)] as const,
  seasons: (pid?: number | null) => ['manage', 'seasons', pidKey(pid)] as const,
  extras: (pid?: number | null) => ['manage', 'extras', pidKey(pid)] as const,
  promos: (pid?: number | null) => ['manage', 'promos', pidKey(pid)] as const,
  messages: ['manage', 'messages'] as const,
  thread: (id: number) => ['manage', 'thread', id] as const,
  reviews: (pid?: number | null, status?: string) => ['manage', 'reviews', pidKey(pid), status ?? 'all'] as const,
  dashboard: ['manage', 'dashboard'] as const,
};

// ─── Dashboard ───────────────────────────────────────────────────────────────
export function useManageDashboard() {
  return useQuery({ queryKey: mk.dashboard, queryFn: () => req<ManageDashboard>('/dashboard') });
}

// ─── Analytics ───────────────────────────────────────────────────────────────
export function useAnalytics(propertyId?: number | null, period: AnalyticsPeriod = '90d') {
  return useQuery({
    queryKey: ['analytics', pidKey(propertyId), period],
    queryFn: async () => {
      const qs = [pidQs(propertyId), `period=${period}`].filter(Boolean).join('&');
      const res = await fetch(`/api/portal/analytics?${qs}`, { credentials: 'same-origin' });
      const data = await res.json() as Analytics & { error?: string };
      if (!res.ok) throw new Error(data.error || 'error');
      return data;
    },
  });
}

// ─── Properties ──────────────────────────────────────────────────────────────
export function useProperties() {
  return useQuery({ queryKey: mk.properties, queryFn: () => req<{ properties: Property[] }>('/properties').then(d => d.properties) });
}

// ─── Active property selection (shared across management pages) ───────────────
const ACTIVE_PROPERTY_KEY = 'kx_active_property';

// 'all' = no property filter (helicopter view). A number = that property.
type ActiveSel = number | 'all';

function readStoredProperty(): ActiveSel {
  try {
    const v = localStorage.getItem(ACTIVE_PROPERTY_KEY);
    if (!v || v === 'all') return 'all';
    return Number(v) || 'all';
  } catch { return 'all'; }
}

// Module-level store shared by EVERY component (the selector and the pages),
// so switching a property in the selector re-renders the page queries too.
// (Previously each useActiveProperty call held its own useState — the selector
// updated only itself + localStorage, and the lists never refreshed.)
let activeSel: ActiveSel = readStoredProperty();
const activeSubs = new Set<() => void>();
function setActiveSel(sel: ActiveSel) {
  activeSel = sel;
  try {
    localStorage.setItem(ACTIVE_PROPERTY_KEY, String(sel));
  } catch { /* storage unavailable */ }
  activeSubs.forEach(fn => fn());
}
function subscribeActive(fn: () => void) {
  activeSubs.add(fn);
  return () => { activeSubs.delete(fn); };
}
function getActiveSel(): ActiveSel { return activeSel; }

/**
 * Shared active-property selection.
 * - requireOne=false (default): 'all' is a valid state → propertyId null (no filter).
 * - requireOne=true (calendar/pricing need exactly ONE property): 'all' resolves
 *   to the first property.
 */
export function useActiveProperty(requireOne = false) {
  const { data: properties, isLoading } = useProperties();
  const sel = useSyncExternalStore(subscribeActive, getActiveSel, getActiveSel);

  // Never point at a deleted property.
  useEffect(() => {
    if (!properties || sel === 'all') return;
    if (!properties.some(p => p.id === sel)) setActiveSel('all');
  }, [properties, sel]);

  const setPropertyId = useCallback((id: number | null) => {
    setActiveSel(id == null ? 'all' : id);
  }, []);

  let propertyId: number | null;
  if (sel === 'all') propertyId = requireOne ? (properties?.[0]?.id ?? null) : null;
  else propertyId = sel;

  return {
    propertyId,
    /** True when the user is on the "all properties" view (no filter). */
    allSelected: sel === 'all' && !requireOne,
    setPropertyId,
    properties: properties ?? [],
    isLoading,
  };
}
export function useSaveProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: Partial<Property> & { id?: number }) =>
      req<{ ok: boolean; id?: number }>('/properties', { method: p.id ? 'PUT' : 'POST', body: JSON.stringify(p) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: mk.properties }); qc.invalidateQueries({ queryKey: mk.dashboard }); },
  });
}
export interface BulkOp {
  ids: number[];
  op: 'price_pct' | 'price_set' | 'min_nights' | 'status' | 'add_season';
  value?: string | number;
  season?: { name: string; start_month: number; end_month: number; price_per_night: number; min_stay: number };
}
export function useBulkProperties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: BulkOp) => req<{ ok: boolean; affected: number }>('/properties-bulk', { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mk.properties });
      qc.invalidateQueries({ queryKey: ['manage', 'seasons'] });
      qc.invalidateQueries({ queryKey: mk.dashboard });
    },
  });
}
export function usePropertyMedia(propertyId: number | null) {
  return useQuery({
    queryKey: ['manage', 'property-media', propertyId],
    queryFn: () => req<{ media: PropertyMedia[] }>(`/property-media?property_id=${propertyId}`).then(d => d.media),
    enabled: !!propertyId,
  });
}
export function useUploadPropertyMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, kind, file }: { propertyId: number; kind: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file); fd.append('property_id', String(propertyId)); fd.append('kind', kind);
      const res = await fetch(`${API}/property-media`, { method: 'POST', body: fd, credentials: 'same-origin' });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || 'upload_failed');
      return data;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['manage', 'property-media', v.propertyId] }),
  });
}
export function useDeletePropertyMedia(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/property-media?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'property-media', propertyId] }),
  });
}

export function useDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/properties?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mk.properties }),
  });
}

// ─── Bookings ────────────────────────────────────────────────────────────────
export function useBookings(status?: string, propertyId?: number | null) {
  return useQuery({
    queryKey: mk.bookings(status, propertyId),
    queryFn: () => {
      const qs = [
        status && status !== 'all' ? `status=${status}` : '',
        pidQs(propertyId),
      ].filter(Boolean).join('&');
      return req<{ bookings: Booking[] }>(`/bookings${qs ? `?${qs}` : ''}`).then(d => d.bookings);
    },
  });
}
export function useUpdateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<Booking> & { id: number }) =>
      req<{ ok: boolean }>(`/bookings/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'bookings'] }); qc.invalidateQueries({ queryKey: mk.dashboard }); },
  });
}
export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, refund }: { id: number; refund?: boolean }) =>
      req<{ ok: boolean; refunded: boolean; refund_error?: boolean }>('/booking-cancel', {
        method: 'POST',
        body: JSON.stringify({ booking_id: id, refund: refund ?? false }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'bookings'] }); qc.invalidateQueries({ queryKey: mk.dashboard }); },
  });
}
// Owner confirms a transfer booking once the wire arrives (pending → confirmed).
export function useConfirmTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      req<{ ok: boolean }>('/booking-confirm', { method: 'POST', body: JSON.stringify({ booking_id: id }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'bookings'] }); qc.invalidateQueries({ queryKey: mk.dashboard }); },
  });
}
// Full detail of one booking: purchased extras, house-rules agreement, deposit claims.
export function useBookingDetail(id: number | null) {
  return useQuery({
    queryKey: id != null ? mk.booking(id) : (['manage', 'booking', 'none'] as const),
    queryFn: () => req<BookingDetail>(`/bookings/${id}`),
    enabled: id != null,
  });
}
// Release a held damage deposit, or re-request one that is scheduled / failed /
// waiting on the guest.
export function useDepositAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, action }: { bookingId: number; action: 'release' | 'request' }) =>
      req<{ ok: boolean; deposit_status: DepositStatus }>('/booking-deposit', {
        method: 'POST',
        body: JSON.stringify({ booking_id: bookingId, action }),
      }),
    onSuccess: (_d, { bookingId }) => {
      qc.invalidateQueries({ queryKey: ['manage', 'bookings'] });
      qc.invalidateQueries({ queryKey: mk.booking(bookingId) });
    },
  });
}
// Claim (part of) a held deposit for damages, with photo evidence.
// Direct fetch: the shared `req` helper JSON-encodes bodies, and this endpoint
// takes multipart/form-data (the browser sets the boundary header itself).
export function useSubmitClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bookingId, amount, description, photos }: {
      bookingId: number; amount: number; description: string; photos: File[];
    }) => {
      const fd = new FormData();
      fd.append('booking_id', String(bookingId));
      fd.append('amount', String(amount));
      fd.append('description', description);
      for (const photo of photos) fd.append('photos', photo);
      const res = await fetch(`${API}/booking-claim`, { method: 'POST', body: fd, credentials: 'same-origin' });
      const data = await res.json() as { ok?: boolean; claim_id?: number; deposit_status?: DepositStatus; amount?: number; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || 'claim_failed');
      return data;
    },
    onSuccess: (_d, { bookingId }) => {
      qc.invalidateQueries({ queryKey: ['manage', 'bookings'] });
      qc.invalidateQueries({ queryKey: mk.booking(bookingId) });
    },
  });
}
export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Partial<Booking>) => req<{ ok: boolean; id: number }>('/bookings', { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'bookings'] }); qc.invalidateQueries({ queryKey: mk.dashboard }); },
  });
}

// ─── Calendar / blocked dates ────────────────────────────────────────────────
export function useBlockedDates(propertyId?: number | null) {
  return useQuery({
    queryKey: mk.blocked(propertyId),
    queryFn: () => {
      const qs = pidQs(propertyId);
      return req<{ blocked: BlockedDate[] }>(`/blocked${qs ? `?${qs}` : ''}`).then(d => d.blocked);
    },
  });
}
export function useCreateBlocked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { start_date: string; end_date: string; reason?: string; property_id?: number }) =>
      req<{ ok: boolean; id: number }>('/blocked', { method: 'POST', body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'blocked'] }),
  });
}
export function useDeleteBlocked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/blocked?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'blocked'] }),
  });
}

// ─── Seasons / pricing ───────────────────────────────────────────────────────
export function useSeasons(propertyId?: number | null) {
  return useQuery({
    queryKey: mk.seasons(propertyId),
    queryFn: () => {
      const qs = pidQs(propertyId);
      return req<{ seasons: Season[] }>(`/seasons${qs ? `?${qs}` : ''}`).then(d => d.seasons);
    },
  });
}
export function useSaveSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: Partial<Season> & { id?: number }) =>
      req<{ ok: boolean; id?: number }>('/seasons', { method: s.id ? 'PUT' : 'POST', body: JSON.stringify(s) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'seasons'] }),
  });
}
export function useDeleteSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/seasons?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'seasons'] }),
  });
}

// ─── Paid extras ─────────────────────────────────────────────────────────────
export function useExtras(propertyId?: number | null) {
  return useQuery({
    queryKey: mk.extras(propertyId),
    queryFn: () => {
      const qs = pidQs(propertyId);
      return req<{ extras: Extra[] }>(`/extras${qs ? `?${qs}` : ''}`).then(d => d.extras);
    },
  });
}
export function useCreateExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (x: {
      property_id: number;
      name: string;
      price: number;
      pricing_type: ExtraPricingType;
      description?: string | null;
      active?: number;
      sort_order?: number;
    }) => req<{ ok: boolean; id: number }>('/extras', { method: 'POST', body: JSON.stringify(x) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'extras'] }),
  });
}
export function useUpdateExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (x: Partial<Extra> & { id: number }) =>
      req<{ ok: boolean }>('/extras', { method: 'PUT', body: JSON.stringify(x) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'extras'] }),
  });
}
export function useDeleteExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/extras?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'extras'] }),
  });
}

// ─── Promo codes ─────────────────────────────────────────────────────────────
export function usePromos(propertyId?: number | null) {
  return useQuery({
    queryKey: mk.promos(propertyId),
    queryFn: () => {
      const qs = pidQs(propertyId);
      return req<{ promos: PromoCode[] }>(`/promos${qs ? `?${qs}` : ''}`).then(d => d.promos);
    },
  });
}
export function useCreatePromo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: {
      code: string;
      discount_percent?: number | null;
      discount_fixed?: number | null;
      valid_from?: string | null;
      valid_until?: string | null;
      max_uses?: number | null;
      property_id?: number | null;
    }) => req<{ ok: boolean; id: number }>('/promos', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'promos'] }),
  });
}
export function useDeletePromo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/promos?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'promos'] }),
  });
}

// ─── Messages ────────────────────────────────────────────────────────────────
export function useMessages() {
  return useQuery({ queryKey: mk.messages, queryFn: () => req<{ messages: GuestMessage[] }>('/messages').then(d => d.messages) });
}
export function useMessageThread(id: number | null) {
  return useQuery({
    queryKey: id ? mk.thread(id) : ['manage', 'thread', 'none'],
    queryFn: () => req<{ message: GuestMessage; replies: MessageReply[] }>(`/messages/${id}`),
    enabled: !!id,
  });
}
export function useReplyMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) =>
      req<{ ok: boolean; id: number }>(`/messages/${id}`, { method: 'POST', body: JSON.stringify({ body }) }),
    onSuccess: (_d, { id }) => { qc.invalidateQueries({ queryKey: mk.thread(id) }); qc.invalidateQueries({ queryKey: mk.messages }); },
  });
}

// ─── Payment settings (tenant-level) ─────────────────────────────────────────
// HOW the owner receives payments — configured once in the Cobros step and
// applied to ALL their properties by the booking engine.
export interface PaymentSettings {
  payment_mode: 'stripe' | 'transfer' | 'both';
  bank_iban: string | null;
  bank_holder: string | null;
  deposit_percent: number | null;
  tourist_tax_per_night: number | null;
  tourist_tax_per_person: number; // 0/1
}

export function usePaymentSettings() {
  return useQuery({
    queryKey: ['manage', 'payment-settings'] as const,
    queryFn: () => req<PaymentSettings>('/payment-settings'),
  });
}
export function useSavePaymentSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: PaymentSettings) =>
      req<{ ok: boolean }>('/payment-settings', { method: 'PUT', body: JSON.stringify(s) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'payment-settings'] }),
  });
}

// ─── Reviews ─────────────────────────────────────────────────────────────────
export function useReviews(propertyId?: number | null, status?: string) {
  return useQuery({
    queryKey: mk.reviews(propertyId, status),
    queryFn: () => {
      const qs = [pidQs(propertyId), status && status !== 'all' ? `status=${status}` : '']
        .filter(Boolean).join('&');
      return req<{ reviews: Review[] }>(`/reviews${qs ? `?${qs}` : ''}`).then(d => d.reviews);
    },
  });
}
export function useCreateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Partial<Review>) =>
      req<{ ok: boolean; id: number }>('/reviews', { method: 'POST', body: JSON.stringify(r) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'reviews'] }),
  });
}
export function useUpdateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: Partial<Review> & { id: number }) =>
      req<{ ok: boolean }>('/reviews', { method: 'PUT', body: JSON.stringify(r) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'reviews'] }),
  });
}
export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/reviews?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'reviews'] }),
  });
}

// ─── SES.Hospedajes (RD 933/2021) ─────────────────────────────────────────────
export interface SesAccount {
  id: number;
  label: string | null;
  environment: 'test' | 'prod';
  codigo_arrendador: string;
  aplicacion: string;
  usuario_ws: string;
  has_password: number;      // 1 = a password is stored (never returned)
  enabled: number;
  last_test_at: string | null;
  last_test_ok: number | null;
}

export interface SesAccountInput {
  id?: number;
  label?: string;
  environment?: 'test' | 'prod';
  codigo_arrendador?: string;
  aplicacion?: string;
  usuario_ws?: string;
  password_ws?: string;      // plaintext in; blank on edit keeps the stored one
  enabled?: boolean;
}

export interface SesSubmitResult {
  ok: boolean;
  estado?: string;
  lote?: string;
  codigoRetorno?: string;
  descripcion?: string;
  error?: string;
  errors?: string[];
}

export function useSesAccounts() {
  return useQuery({
    queryKey: ['manage', 'ses', 'accounts'],
    queryFn: () => req<{ accounts: SesAccount[] }>('/ses/accounts').then(d => d.accounts),
  });
}

export function useSaveSesAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: SesAccountInput) =>
      req<{ ok: boolean; id?: number }>('/ses/accounts', {
        method: v.id ? 'PUT' : 'POST',
        body: JSON.stringify(v),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'ses', 'accounts'] }),
  });
}

export function useDeleteSesAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/ses/accounts?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'ses', 'accounts'] }),
  });
}

export function useTestSesAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      req<{ ok: boolean; httpStatus: number; codigoRetorno: string; descripcion: string }>('/ses/test', {
        method: 'POST', body: JSON.stringify({ id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'ses', 'accounts'] }),
  });
}

// Manual send (or re-check) of a booking's parte de viajeros. Invalidates every
// bookings list so the Reservas status column refreshes.
export function useSesSubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { booking_id: number; action?: 'send' | 'check' }) =>
      req<SesSubmitResult>('/ses/submit', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'bookings'] }),
  });
}

// ─── Guest directory ──────────────────────────────────────────────────────────
export interface GuestRow {
  guest_key: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  language: string | null;
  bookings_count: number;
  stays_count: number;
  total_spent: number | null;
  total_nights: number | null;
  first_stay: string | null;
  last_stay: string | null;
  upcoming: number;
  has_registration: number;
  properties: string | null;   // comma-separated distinct property names
  doc_number: string | null;
  nationality: string | null;
}

export function useGuests() {
  return useQuery({
    queryKey: ['manage', 'guests'],
    queryFn: () => req<{ guests: GuestRow[] }>('/guests').then(d => d.guests),
  });
}

// ─── Automated-message templates + schedules (migration 052) ─────────────────
export interface MessageTemplateSummary {
  trigger_key: string; lang: string; subject: string; enabled: number; updated_at: string;
}
export interface MessageTemplateFull extends MessageTemplateSummary {
  body_json: string | null; body_html: string;
}
export interface MessageSchedule {
  trigger_key: string; anchor: 'check_in' | 'check_out'; offset_days: number; enabled: number; is_default?: boolean;
}

export function useMessageTemplates() {
  return useQuery({
    queryKey: ['manage', 'msg-templates'],
    queryFn: () => req<{ templates: MessageTemplateSummary[] }>('/message-templates').then(d => d.templates),
  });
}
export function useMessageTemplate(triggerKey: string | null, lang = 'es') {
  return useQuery({
    queryKey: ['manage', 'msg-template', triggerKey, lang],
    enabled: !!triggerKey,
    queryFn: () => req<{ template: MessageTemplateFull | null }>(`/message-templates?trigger_key=${triggerKey}&lang=${lang}`).then(d => d.template),
  });
}
export function useSaveMessageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { trigger_key: string; lang?: string; subject: string; body_json?: unknown; body_html: string; enabled?: boolean }) =>
      req<{ ok: boolean }>('/message-templates', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'msg-templates'] }); qc.invalidateQueries({ queryKey: ['manage', 'msg-template'] }); },
  });
}
export function useDeleteMessageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { trigger_key: string; lang?: string }) =>
      req<{ ok: boolean }>(`/message-templates?trigger_key=${v.trigger_key}&lang=${v.lang ?? 'es'}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'msg-templates'] }); qc.invalidateQueries({ queryKey: ['manage', 'msg-template'] }); },
  });
}
export function useMessageSchedules() {
  return useQuery({
    queryKey: ['manage', 'msg-schedules'],
    queryFn: () => req<{ schedules: MessageSchedule[] }>('/message-schedules').then(d => d.schedules),
  });
}
export function useSaveMessageSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { trigger_key: string; offset_days: number; enabled?: boolean }) =>
      req<{ ok: boolean }>('/message-schedules', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'msg-schedules'] }),
  });
}
export function useTestMessageTemplate() {
  return useMutation({
    mutationFn: (v: { trigger_key: string; to?: string; lang?: string }) =>
      req<{ ok: boolean; to: string }>('/message-templates/test', { method: 'POST', body: JSON.stringify(v) }),
  });
}

// ─── Newsletter ───────────────────────────────────────────────────────────────
export interface Subscriber {
  id: number; email: string; name: string | null; lang: string | null;
  consent_source: string; consent_at: string; unsubscribed_at: string | null; created_at: string;
}
export interface Campaign {
  id: number; subject: string; status: 'draft' | 'sending' | 'sent';
  sent_at: string | null; sent_count: number; created_at: string; updated_at: string;
  pending_count?: number;
  body_json?: string | null; body_html?: string | null;
}

export function useSubscribers() {
  return useQuery({
    queryKey: ['manage', 'nl-subscribers'],
    queryFn: () => req<{ subscribers: Subscriber[] }>('/newsletter/subscribers').then(d => d.subscribers),
  });
}
export function useAddSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; name?: string; lang?: string }) =>
      req<{ ok: boolean }>('/newsletter/subscribers', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'nl-subscribers'] }),
  });
}
export function useImportGuests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      req<{ ok: boolean; imported: number; total: number }>('/newsletter/subscribers', { method: 'POST', body: JSON.stringify({ action: 'import_guests' }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'nl-subscribers'] }),
  });
}
export function useDeleteSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/newsletter/subscribers?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'nl-subscribers'] }),
  });
}
export function useCampaigns() {
  return useQuery({
    queryKey: ['manage', 'nl-campaigns'],
    queryFn: () => req<{ campaigns: Campaign[] }>('/newsletter/campaigns').then(d => d.campaigns),
  });
}
export function useCampaign(id: number | null) {
  return useQuery({
    queryKey: ['manage', 'nl-campaign', id],
    enabled: !!id,
    queryFn: () => req<{ campaign: Campaign }>(`/newsletter/campaigns?id=${id}`).then(d => d.campaign),
  });
}
export function useSaveCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: number; subject: string; body_json?: unknown; body_html?: string }) =>
      req<{ ok: boolean; id?: number }>('/newsletter/campaigns', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'nl-campaigns'] }); qc.invalidateQueries({ queryKey: ['manage', 'nl-campaign'] }); },
  });
}
export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/newsletter/campaigns?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'nl-campaigns'] }),
  });
}
export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaign_id: number) =>
      req<{ ok: boolean; status: string; sent: number; remaining: number }>('/newsletter/send', { method: 'POST', body: JSON.stringify({ campaign_id }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'nl-campaigns'] }),
  });
}

// ─── Custom automated messages (migration 053) ────────────────────────────────
export interface CustomMessageRow {
  id: number; name: string; anchor: 'check_in' | 'check_out'; offset_days: number; enabled: number; langs: string | null;
}
export interface CustomMessageTemplate { lang: string; subject: string; body_json: string | null; body_html: string }

export function useCustomMessages() {
  return useQuery({
    queryKey: ['manage', 'custom-msgs'],
    queryFn: () => req<{ messages: CustomMessageRow[] }>('/custom-messages').then(d => d.messages),
  });
}
export function useCustomMessage(id: number | null) {
  return useQuery({
    queryKey: ['manage', 'custom-msg', id],
    enabled: !!id,
    queryFn: () => req<{ message: CustomMessageRow; templates: CustomMessageTemplate[] }>(`/custom-messages?id=${id}`),
  });
}
export function useSaveCustomMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: number; name?: string; anchor?: string; offset_days?: number; enabled?: boolean }) =>
      req<{ ok: boolean; id?: number }>('/custom-messages', { method: v.id ? 'PUT' : 'POST', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'custom-msgs'] }); qc.invalidateQueries({ queryKey: ['manage', 'custom-msg'] }); },
  });
}
export function useDeleteCustomMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/custom-messages?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manage', 'custom-msgs'] }),
  });
}
export function useSaveCustomMessageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { custom_message_id: number; lang: string; subject: string; body_json?: unknown; body_html: string }) =>
      req<{ ok: boolean }>('/custom-messages/template', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'custom-msgs'] }); qc.invalidateQueries({ queryKey: ['manage', 'custom-msg'] }); },
  });
}
export function useTestCustomMessage() {
  return useMutation({
    mutationFn: (v: { id: number; to?: string; lang?: string }) =>
      req<{ ok: boolean; to: string }>('/custom-messages/test', { method: 'POST', body: JSON.stringify(v) }),
  });
}

// ─── Property owners (agencies) — migration 054 ───────────────────────────────
export interface Owner {
  id: number; name: string; email: string | null; phone: string | null; notes: string | null;
  created_at: string; property_count: number;
}
export function useOwners() {
  return useQuery({
    queryKey: ['manage', 'owners'],
    queryFn: () => req<{ owners: Owner[] }>('/owners').then(d => d.owners),
  });
}
export function useSaveOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: number; name?: string; email?: string; phone?: string; notes?: string }) =>
      req<{ ok: boolean; id?: number }>('/owners', { method: v.id ? 'PUT' : 'POST', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'owners'] }); qc.invalidateQueries({ queryKey: mk.properties }); },
  });
}
export function useDeleteOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => req<{ ok: boolean }>(`/owners?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['manage', 'owners'] }); qc.invalidateQueries({ queryKey: mk.properties }); },
  });
}
